import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../../../../components';
import { getFullDateTime } from '../../../../../config/utils/dateHelpers';
import { MONEY_COLLECTION_TRADER_TYPE_LABEL } from '../../../../../interfaces/moneyCollection.interface';
import { CustomerReportResponse } from '../../../../../interfaces/reports.interface';
import { getCustomerVatNumberForMyData } from '../../../../../services/utils/myDataUtils';

interface Props {
  data: CustomerReportResponse;
}

export const CustomerReportPdfTrader = ({ data }: Props) => {
  const { t } = useTranslation();

  const getCustomerSection = useCallback(() => {
    return (
      <View style={styles.row}>
        <View style={styles.column}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={[pdfStyles.smallTitle, styles.paddingRight4]}>{t('PDF.TRADER')}</Text>
            <Text style={pdfStyles.smallTitleRegular}>
              ({t(`PDF.${MONEY_COLLECTION_TRADER_TYPE_LABEL[data?.params?.traderType || 1]}`)})
            </Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={pdfStyles.body}>{data?.params?.customer?.companyName}</Text>
          </View>
        </View>
        <View style={styles.columnCenter}>
          <Text style={pdfStyles.smallTitle}>{t('PDF.DATE_LOWER')}</Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={pdfStyles.body}>{getFullDateTime(new Date().toISOString())}</Text>
          </View>
        </View>
        <View style={styles.columnEnd}>
          <Text style={pdfStyles.smallTitle}>{t('PDF.VAT_NUMBER')}</Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={pdfStyles.body}>{getCustomerVatNumberForMyData(data?.params?.customer)}</Text>
          </View>
        </View>
      </View>
    );
  }, [data?.params?.customer?.companyName, data?.params?.customer?.vatNumber, data?.params?.traderType, t]);

  const getParamsSection = useCallback(() => {
    return (
      <View style={[styles.row, styles.paddingTop10]}>
        <View style={[styles.column, { justifyContent: 'center' }]}>
          {data?.params?.includeMoneyCollection && (
            <Text style={pdfStyles.smallTitle}>{t('PDF.WITH_MONEY_COLLECTION')}</Text>
          )}
        </View>
        <View style={styles.columnCenter}>
          <Text style={pdfStyles.smallTitle}>
            {t('PDF.START_DATE')} - {t('PDF.END_DATE')}
          </Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={pdfStyles.body}>
              {data?.params?.start} - {data?.params?.end}
            </Text>
          </View>
        </View>
        <View style={[styles.columnEnd, { justifyContent: 'center' }]}>
          {data?.params?.includeDeliveryNotes && (
            <Text style={pdfStyles.smallTitle}>{t('PDF.WITH_DELIVERY_NOTES')}</Text>
          )}
        </View>
      </View>
    );
  }, [
    data?.params?.end,
    data?.params?.includeDeliveryNotes,
    data?.params?.includeMoneyCollection,
    data?.params?.start,
    t,
  ]);

  return (
    <View style={pdfStyles.column}>
      {getCustomerSection()}
      {getParamsSection()}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 10,
  },
  column: {
    flexDirection: 'column',
    width: '50%',
    alignItems: 'flex-start',
  },
  columnCenter: {
    flexDirection: 'column',
    width: '33%',
    alignItems: 'center',
  },
  columnEnd: {
    flexDirection: 'column',
    width: '50%',
    alignItems: 'flex-end',
  },
  paddingRight4: {
    paddingRight: 4,
  },
  paddingTop10: {
    paddingTop: 10,
  },
});
